import React, { useEffect } from "react";
import { useForm, UseFormRegister, FieldErrors } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { useGetUserProfileQuery } from "../../../../api/userAuthApi";
import { useUpdateProfile } from "../../../../hooks/useUpdateProfile";
import {
  createAccountDetailsSchema,
  AccountDetailsFormData,
} from "../../../../helpers/userValidation";
import PasswordRequirements from "../../../common/PasswordRequirements";
import Skeleton from "react-loading-skeleton";

// =============================================================================
// 1. LOCAL SUB-COMPONENTS
// Defined here for better organization and readability of the main component.
// =============================================================================

interface FieldProps {
  register: UseFormRegister<AccountDetailsFormData>;
  errors: FieldErrors<AccountDetailsFormData>;
}

// Extend for PasswordFields to accept current value
interface PasswordFieldsProps extends FieldProps {
  newPassword?: string;
}

/**
 * Renders the First Name, Last Name, and Display Name fields.
 */
const PersonalInfoFields: React.FC<FieldProps> = ({ register, errors }) => {
  const { t } = useTranslation();
  return (
    <>
      <div className="checkout-form__grid">
        <div className="form-field">
          <label>{t("checkoutForm.fields.firstName")}</label>
          <input
            {...register("first_name")}
            className={errors.first_name ? "has-error" : ""}
          />
          {errors.first_name && (
            <p className="form-field__error">{errors.first_name.message}</p>
          )}
        </div>
        <div className="form-field">
          <label>{t("checkoutForm.fields.lastName")}</label>
          <input
            {...register("last_name")}
            className={errors.last_name ? "has-error" : ""}
          />
          {errors.last_name && (
            <p className="form-field__error">{errors.last_name.message}</p>
          )}
        </div>
      </div>

      <div className="form-field">
        <label>{t("user.accountDetails.displayName")}</label>
        <input
          {...register("display_name")}
          className={errors.display_name ? "has-error" : ""}
        />
        {errors.display_name && (
          <p className="form-field__error">{errors.display_name.message}</p>
        )}
        <small style={{ color: "var(--color-text-muted)" }}>
          {t("user.accountDetails.displayNameNotice")}
        </small>
      </div>
    </>
  );
};

/**
 * Renders the Email field.
 */
const EmailField: React.FC<FieldProps> = ({ register, errors }) => {
  const { t } = useTranslation();
  return (
    <div className="form-field">
      <label>{t("checkoutForm.fields.email")}</label>
      <input
        type="email"
        {...register("email")}
        className={errors.email ? "has-error" : ""}
      />
      {errors.email && (
        <p className="form-field__error">{errors.email.message}</p>
      )}
    </div>
  );
};

/**
 * Renders the Password Change fieldset.
 */
const PasswordFields: React.FC<PasswordFieldsProps> = ({
  register,
  errors,
  newPassword,
}) => {
  const { t } = useTranslation();
  return (
    <fieldset
      style={{
        marginTop: "2rem",
        border: "1px solid var(--color-border-light)",
        padding: "1.5rem",
        borderRadius: "var(--border-radius-md)",
      }}
    >
      <legend
        style={{
          padding: "0 0.5rem",
          fontWeight: "bold",
          color: "var(--color-text-primary)",
        }}
      >
        {t("user.accountDetails.passwordChange")}
      </legend>
      <div className="form-field">
        <label>{t("user.accountDetails.currentPassword")}</label>
        <input
          type="password"
          {...register("password_current")}
          className={errors.password_current ? "has-error" : ""}
        />
        {errors.password_current && (
          <p className="form-field__error">{errors.password_current.message}</p>
        )}
      </div>
      <div className="form-field">
        <label>{t("user.accountDetails.newPassword")}</label>
        <input
          type="password"
          {...register("password_new")}
          className={errors.password_new ? "has-error" : ""}
        />
        <PasswordRequirements
          password={newPassword}
          isVisible={!!newPassword}
        />
        {errors.password_new && (
          <p className="form-field__error">{errors.password_new.message}</p>
        )}
      </div>
      <div className="form-field">
        <label>{t("user.accountDetails.confirmPassword")}</label>
        <input
          type="password"
          {...register("password_confirm")}
          className={errors.password_confirm ? "has-error" : ""}
        />
        {errors.password_confirm && (
          <p className="form-field__error">{errors.password_confirm.message}</p>
        )}
      </div>
    </fieldset>
  );
};

// =============================================================================
// 2. MAIN COMPONENT
// Orchestrates the sub-components.
// =============================================================================
const AccountDetails: React.FC = () => {
  const { t } = useTranslation();
  const { data: profile, isLoading: isProfileLoading } =
    useGetUserProfileQuery();
  const { handleUpdate, isLoading: isUpdating } = useUpdateProfile();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm<AccountDetailsFormData>({
    resolver: zodResolver(createAccountDetailsSchema(t)),
    defaultValues: {
      first_name: profile?.firstName || "",
      last_name: profile?.lastName || "",
      display_name: profile?.displayName || "",
      email: profile?.email || "",
      password_current: "",
      password_new: "",
      password_confirm: "",
    },
  });

  const newPasswordValue = watch("password_new");

  // Reset form when profile data loads
  useEffect(() => {
    if (profile) {
      reset({
        first_name: profile.firstName,
        last_name: profile.lastName,
        display_name: profile.displayName,
        email: profile.email,
        password_current: "",
        password_new: "",
        password_confirm: "",
      });
    }
  }, [profile, reset]);

  const onSubmit = async (data: AccountDetailsFormData) => {
    await handleUpdate(data);
  };

  if (isProfileLoading) {
    return (
      <div style={{ padding: "2rem" }}>
        <Skeleton count={5} height={40} style={{ marginBottom: "1rem" }} />
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="auth-form">
      <h2 className="profile-section-title">
        {t("user.accountDetails.title")}
      </h2>

      <PersonalInfoFields register={register} errors={errors} />

      <EmailField register={register} errors={errors} />

      <PasswordFields
        register={register}
        errors={errors}
        newPassword={newPasswordValue}
      />

      <button
        type="submit"
        className="button button--primary"
        disabled={isUpdating}
        style={{ marginTop: "1.5rem" }}
      >
        {isUpdating
          ? t("user.accountDetails.saving")
          : t("user.accountDetails.saveChanges")}
      </button>
    </form>
  );
};

export default AccountDetails;
